import { useState } from "react";
import { classNames } from "../../../utils/cn";
import {
  DealActivityType,
  useSimilarDataRoomFilesQuery,
  DataRoomFileOnlineStatus,
  DataRoomFileSimilarityStatus,
  PermissionCheckInput,
  DealRoleType,
  PermissionsResourceType,
  useDataRoomFileOverviewQuery,
  useDataRoomFileCommentsQuery,
  useDataRoomFileLinkedQuestionsQuery,
  useDataRoomFileSettingsQuery,
  DataRoomFileLinkedQuestionsQuery,
} from "../../../graphql/generated";
import { formatDistanceToNow, fromUnixTime } from "date-fns";
import { useParams } from "react-router-dom";
import { DeleteFile } from "../DeleteFileModal";
import { Link, useHistory } from "react-router-dom";
import { EditFile } from "../../../components/data_room/EditFile";
import { Card } from "../../../components/Card";
import { Avatar } from "../../../components/account/Avatar";
import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import Loading from "../../../components/Loading";
import { Activity } from "../../../components/activity/Activity";
import { CommentInput } from "@/src/components/activity/CommentInput";
import { useQueryClient } from "@tanstack/react-query";
import { ActivityItems } from "@/src/components/activity/ActivityItems";
import { InfoIcon } from "lucide-react";
import useGqlClient from "@/src/hooks/useGqlClient";
import { links } from "@/src/utils/links";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";

interface FileTab {
  name: string;
  permissionCheck: PermissionCheckInput;
}

interface Props {
  fileId: string;
  deletedAt?: number | null;
  onlineStatus: DataRoomFileOnlineStatus;
}

export function Tabs(props: Props) {
  const tabs: FileTab[] = [
    {
      name: "Overview",
      permissionCheck: {
        permissionKey: "documents:view",
        roleType: DealRoleType.Internal,
        resource: {
          resourceID: props.fileId,
          resourceType: PermissionsResourceType.Document,
        },
      },
    },
    {
      name: "Comments",
      permissionCheck: {
        permissionKey: "documents:view",
        resource: {
          resourceID: props.fileId,
          resourceType: PermissionsResourceType.Document,
        },
      },
    },
    {
      name: "Questions",
      permissionCheck: {
        permissionKey: "documents:view",
        resource: {
          resourceID: props.fileId,
          resourceType: PermissionsResourceType.Document,
        },
      },
    },
    {
      name: "Settings",
      permissionCheck: {
        permissionKey: "documents:edit",
        resource: {
          resourceID: props.fileId,
          resourceType: PermissionsResourceType.Document,
        },
      },
    },
  ];

  return <TabContent fileId={props.fileId} deletedAt={props.deletedAt} tabs={tabs} />;
}

function TabContent(props: {
  fileId: string;
  deletedAt?: number | null;
  tabs: FileTab[];
}) {
  const client = useGqlClient();
  const [selectedTab, setSelectedTab] = useState(props.tabs[0].name);
  const tabs = props.deletedAt ? [] : props.tabs;

  // Shares cache with Questions tab; badge appears after that tab has loaded once
  const questionsQuery = useDataRoomFileLinkedQuestionsQuery(
    client,
    { id: props.fileId },
    {
      enabled: selectedTab === "Questions" && !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );
  const questionsCount = questionsQuery.data
    ? questionsQuery.data.dataRoomFile.questions.length +
      questionsQuery.data.dataRoomFile.answers.length
    : 0;

  return (
    <>
      <div className="sm:hidden">
        <label htmlFor="tabs" className="sr-only">
          Select a tab
        </label>
        <select
          id="tabs"
          name="tabs"
          className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
          value={selectedTab}
          onChange={(e) => {
            setSelectedTab(e.currentTarget.value);
          }}
        >
          {tabs.map((tab) => (
            <PermissionCheck key={tab.name} input={tab.permissionCheck}>
              <option key={tab.name}>{tab.name}</option>
            </PermissionCheck>
          ))}
        </select>
      </div>
      <div className="hidden sm:block">
        <nav className="flex space-x-4" aria-label="Tabs">
          {tabs.map((tab) => (
            <PermissionCheck key={tab.name} input={tab.permissionCheck}>
              <button
                onClick={() => {
                  setSelectedTab(tab.name);
                  setTimeout(() => {
                    window.scrollTo({
                      top: document.body.scrollHeight,
                      behavior: "smooth",
                    });
                  }, 100);
                }}
                key={tab.name}
                className={classNames(
                  tab.name === selectedTab
                    ? "bg-gray-200/60 text-gray-700"
                    : "text-gray-500 hover:text-gray-700",
                  "rounded-md px-3 py-2 text-sm font-semibold relative flex items-center gap-x-1.5",
                )}
                aria-current={tab.name === selectedTab ? "page" : undefined}
              >
                {tab.name}
                {tab.name === "Questions" && questionsCount > 0 && (
                  <span className="inline-flex items-center justify-center p-2 w-2 h-2 text-xs font-semibold text-gray-700 bg-gray-300 rounded-full">
                    {questionsCount}
                  </span>
                )}
              </button>
            </PermissionCheck>
          ))}
        </nav>
      </div>
      <div className="mt-3 mb-12">
        <TabRenderer
          selectedTab={selectedTab}
          fileId={props.fileId}
          setSelectedTab={setSelectedTab}
        />
      </div>
    </>
  );
}

function TabRenderer(props: {
  selectedTab: string;
  fileId: string;
  setSelectedTab: (tab: string) => void;
}) {
  switch (props.selectedTab) {
    case "Overview":
      return <Overview fileId={props.fileId} />;
    case "Comments":
      return <Comments fileId={props.fileId} />;
    case "Settings":
      return <Settings fileId={props.fileId} />;
    case "Questions":
      return <Questions fileId={props.fileId} />;
    case "Similar Documents":
      return <SimilarDocuments fileId={props.fileId} />;
    default:
      return <Overview fileId={props.fileId} />;
  }
}

const stats = ["Views", "Downloads", "Versions"];

function Overview(props: { fileId: string }) {
  const client = useGqlClient();
  const overviewQuery = useDataRoomFileOverviewQuery(
    client,
    { id: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (overviewQuery.isPending || !overviewQuery.data) {
    return <Loading />;
  }

  const file = overviewQuery.data.dataRoomFile;

  return (
    <div>
      <PermissionCheck input={{ permissionKey: "deal_activity:has_access" }}>
        <dl className="mt-2 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-x md:divide-y-0">
          {stats.map((item) => (
            <div key={item} className="px-4 py-5 sm:p-6">
              <dt className="text-base font-normal text-gray-900">{item}</dt>
              <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                <div className="flex items-baseline text-2xl font-semibold text-gray-700">
                  {item === "Views"
                    ? file.stats.views
                    : item === "Downloads"
                      ? file.stats.downloads
                      : file.versions.length}
                </div>
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-8">
          <p className="font-semibold text-gray-700">Activity</p>
          <Activity
            input={{
              dataRoomFileID: props.fileId,
              count: 30,
            }}
            mode="verbose"
          />
        </div>
      </PermissionCheck>
    </div>
  );
}

function Comments(props: { fileId: string }) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const commentsQuery = useDataRoomFileCommentsQuery(
    client,
    { id: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (commentsQuery.isPending || !commentsQuery.data) {
    return <Loading />;
  }

  const comments = commentsQuery.data.dataRoomFile.activity
    .filter((a) => a.type === DealActivityType.Comment)
    .sort((a, b) => a.createdAt - b.createdAt);

  return (
    <div>
      <div className="mt-3 mx-3 px-3 flex items-center gap-x-1 rounded-md bg-gray-100 py-2 border border-gray-300">
        <InfoIcon className="w-4 h-4 text-gray-500" />
        <p className="text-gray-500 font-semibold text-sm">
          Comments are only visible to people in your firm
        </p>
      </div>
      <div className="mt-4 px-3">
        <ActivityItems items={comments} />

        <CommentInput
          input={{
            dataRoomFileId: props.fileId,
            comment: "",
            files: [],
          }}
          onCommented={() => {
            queryClient.invalidateQueries({
              queryKey: ["DataRoomFileComments", { id: props.fileId }],
            });
          }}
        />
      </div>
    </div>
  );
}

function Settings(props: { fileId: string }) {
  const client = useGqlClient();
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();
  const [showDeleteFile, setShowDeleteFile] = useState(false);
  const [showEditFile, setShowEditFile] = useState(false);

  const settingsQuery = useDataRoomFileSettingsQuery(
    client,
    { id: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (settingsQuery.isPending || !settingsQuery.data) {
    return <Loading />;
  }

  const file = settingsQuery.data.dataRoomFile;

  return (
    <Card padding="m">
      <div className="grid grid-cols-1 gap-y-4 xl:gap-y-0 xl:grid-cols-2">
        <div>
          <p className="mt-2 text-sm font-semibold text-gray-600">
            Permission level
          </p>
          <div className="mt-1 ">
            <p className="text-sm font-semibold text-gray-700">
              {file.permission.name}
            </p>
            <p className="text-xs text-gray-500/80">
              {file.permission.description}
            </p>
          </div>
        </div>

        <div>
          <p className="mt-2 text-sm font-semibold text-gray-600">
            Description
          </p>
          <div className="mt-1 flex flex-wrap">
            <p className="text-sm text-gray-500/80">
              {file.description ? file.description : "No file description"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 ">
        <div className="w-full h-px bg-gray-200 my-4"></div>
        <PermissionCheck
          input={{
            permissionKey: "documents:edit",
            resource: {
              resourceID: props.fileId,
              resourceType: PermissionsResourceType.Document,
            },
          }}
        >
          <button
            onClick={() => {
              setShowEditFile(true);
            }}
            className="text-blue-700 text-sm px-2 py-1.5 rounded-md hover:bg-blue-50 font-semibold"
          >
            Edit file
          </button>
        </PermissionCheck>
        <PermissionCheck
          input={{
            permissionKey: "documents:delete",
            resource: {
              resourceID: props.fileId,
              resourceType: PermissionsResourceType.Document,
            },
          }}
        >
          <button
            onClick={() => {
              setShowDeleteFile(true);
            }}
            className="text-red-700 text-sm ml-4 px-2 py-1.5 rounded-md hover:bg-red-50 font-semibold"
          >
            Delete file
          </button>
        </PermissionCheck>
      </div>
      <DeleteFile
        open={showDeleteFile}
        dataRoomFileId={props.fileId}
        onDeleted={() => {
          history.push(`/deal/${dealId}/documents/`);
        }}
        onClose={() => {
          setShowDeleteFile(false);
        }}
      />
      <EditFile
        dataRoomFileId={props.fileId}
        open={showEditFile}
        onClose={() => {
          setShowEditFile(false);
        }}
      />
    </Card>
  );
}

function Questions(props: { fileId: string }) {
  const client = useGqlClient();
  const questionsQuery = useDataRoomFileLinkedQuestionsQuery(
    client,
    { id: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (questionsQuery.isPending || !questionsQuery.data) {
    return <Loading />;
  }

  const file = questionsQuery.data.dataRoomFile;
  const totalQuestions = file.questions.length + file.answers.length;

  return (
    <div className="mb-12">
      {totalQuestions === 0 ? (
        <p className="px-3 text-gray-500/80 text-sm">
          Document not referenced in any questions
        </p>
      ) : (
        <Card margin="s 0 0 0">
          {file.questions.map((question, i) => (
            <div key={question.id}>
              <QuestionRow
                question={question}
                isFirst={i === 0}
                isLast={i === file.questions.length - 1}
              />
              {i !== file.questions.length - 1 ? (
                <div className="border-b border-gray-200" />
              ) : null}
            </div>
          ))}
          {file.answers.map((answer) => (
            <AnswerRow key={answer.id} answer={answer} />
          ))}
        </Card>
      )}
    </div>
  );
}

function AnswerRow(props: {
  answer: DataRoomFileLinkedQuestionsQuery["dataRoomFile"]["answers"][0];
}) {
  const { dealId } = useParams<{ dealId: string }>();
  return (
    <div
      key={props.answer.id}
      className={`w-full 3xl:w-4/5 hover:bg-gray-200/70 cursor-pointer first:rounded-t-md last:rounded-b-md`}
    >
      <Link to={links.questionLink(props.answer.question, dealId)}>
        <div className="flex px-3 py-2 items-center space-x-3">
          <Avatar account={props.answer.answeredBy} />
          <div className="flex-1">
            <p className="text-gray-700 text-sm font-medium">
              Referenced as an answer to{" "}
              <span className="font-semibold">
                {props.answer.question.title}
              </span>
            </p>
            <p className="text-xs text-gray-500 leading-tight text-ellipsis">
              Answered{" "}
              {formatDistanceToNow(fromUnixTime(props.answer.createdAt), {
                addSuffix: true,
              })}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
}

function QuestionRow(props: {
  question: DataRoomFileLinkedQuestionsQuery["dataRoomFile"]["questions"][0];
  isFirst: boolean;
  isLast: boolean;
}) {
  const { dealId } = useParams<{ dealId: string }>();

  return (
    <div
      key={props.question.id}
      className={`w-full 3xl:w-4/5 hover:bg-gray-200/70 cursor-pointer ${
        props.isFirst ? "rounded-t-md" : ""
      } ${props.isLast ? "rounded-b-md" : ""}`}
    >
      <Link to={links.questionLink(props.question, dealId)}>
        <div className="flex px-3 py-2 items-center space-x-3">
          <Avatar account={props.question.createdBy} />
          <div className="flex-1">
            <p className="text-gray-700 font-semibold text-sm">
              {props.question.title}
            </p>
            <p className="text-xs text-gray-500 leading-tight text-ellipsis">
              Created{" "}
              {formatDistanceToNow(fromUnixTime(props.question.createdAt), {
                addSuffix: true,
              })}
            </p>
          </div>
          <div className="flex gap-x-2">
            <ChatBubbleLeftIcon className="mt-0.5 w-5 h-5 text-gray-500" />
            <p className="text-gray-600">
              {
                props.question.activity.filter(
                  (a) => a.type === DealActivityType.Comment,
                ).length
              }
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
}

function SimilarDocuments(props: { fileId: string }) {
  const client = useGqlClient();
  const history = useHistory();
  const { dealId } = useParams<{ dealId: string }>();

  const similarQuery = useSimilarDataRoomFilesQuery(
    client,
    { dataRoomFileID: props.fileId },
    {
      enabled: !!props.fileId,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  if (similarQuery.isPending || !similarQuery.data) {
    return <Loading />;
  }

  const allSimilarFiles = similarQuery.data.similarDataRoomFiles;

  const processedFiles = allSimilarFiles.reduce((acc: any[], file: any) => {
    let similarFile: any;
    if (file.dataRoomFile1?.id === props.fileId) {
      similarFile = file.dataRoomFile2;
    } else if (file.dataRoomFile2?.id === props.fileId) {
      similarFile = file.dataRoomFile1;
    } else {
      return acc;
    }

    if (!similarFile) {
      return acc;
    }

    const existingIndex = acc.findIndex(
      (item) => item.similarFile?.id === similarFile.id,
    );

    if (existingIndex >= 0) {
      if (file.similarity > acc[existingIndex].similarity) {
        acc[existingIndex] = { ...file, similarFile };
      }
    } else {
      acc.push({ ...file, similarFile });
    }

    return acc;
  }, []);

  if (processedFiles.length === 0) {
    return (
      <div className="mt-3 mx-3 px-3 flex items-center gap-x-1 rounded-md bg-gray-100 py-2 border border-gray-300">
        <InfoIcon className="w-4 h-4 text-gray-500" />
        <p className="text-gray-500 font-semibold text-sm">
          No similar documents found
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mt-3 mx-3 px-3 flex items-center gap-x-1 bg-white rounded-md p-2 border border-gray-200 overflow-hidden">
        <table className="min-w-full rounded-lg border-separate border-spacing-0 rounded-b-lg text-sm">
          <thead className="font-semibold">
            <tr>
              <th scope="col" className="py-3 pl-3 pr-8 text-left">
                File Name
              </th>
              <th scope="col" className="px-3 py-3.5 text-left">
                Created On
              </th>
              <th scope="col" className="px-3 py-3.5 text-left">
                Similarity Score
              </th>
              <th scope="col" className="px-3 py-3.5 text-left">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="rounded-b-lg text-sm text-gray-600">
            {processedFiles.map((file: any, index: number) => {
              const isLast = index === processedFiles.length - 1;
              const similarFile = file.similarFile;

              return (
                <tr
                  key={file.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    const link = `/deal/${dealId}/documents/folder/${similarFile.folderId}/file/${similarFile.id}`;
                    history.push(link);
                  }}
                >
                  <td
                    className={classNames(
                      "whitespace-nowrap py-3 pl-3 pr-3",
                      isLast ? "" : "border-b border-gray-200",
                    )}
                  >
                    {similarFile.name}
                  </td>
                  <td
                    className={classNames(
                      "whitespace-nowrap px-3 py-3",
                      isLast ? "" : "border-b border-gray-200",
                    )}
                  >
                    {similarFile.createdAt
                      ? formatDistanceToNow(
                          fromUnixTime(similarFile.createdAt),
                          { addSuffix: true },
                        )
                      : "-"}
                  </td>
                  <td
                    className={classNames(
                      "whitespace-nowrap px-3 py-3",
                      isLast ? "" : "border-b border-gray-200",
                    )}
                  >
                    {Math.round(file.similarity * 100)}%
                  </td>
                  <td
                    className={classNames(
                      "whitespace-nowrap px-3 py-3",
                      isLast ? "" : "border-b border-gray-200",
                    )}
                  >
                    {file.status === DataRoomFileSimilarityStatus.Detected
                      ? "Detected"
                      : file.status}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
